import React, { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { Mail, Clock, Save } from "lucide-react";

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AdminEmailSchedule() {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // job_id being saved

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-schedule");
      setJobs(r.data?.jobs || []);
      setCategories(r.data?.categories || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchJob = async (jobId, patch) => {
    setBusy(jobId);
    try {
      const r = await api.patch(`/admin/email-schedule/${jobId}`, patch);
      toast.success(r.data?.reload_required || "Saved — scheduler picks it up within 60s");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        <div className="overline">COMMUNICATION</div>
        <h1 className="font-display text-3xl lg:text-4xl mt-1">Email schedule</h1>
        <div className="text-sm text-ink-soft mt-1 mb-6">
          Toggle any recurring email job on or off. Change the hour, day-of-week or minute and the scheduler
          reloads automatically within 60 seconds — no restart needed.
        </div>

        <Card testId="schedule-jobs-card">
          {loading ? (
            <div className="text-sm text-ink-muted italic py-6">Loading schedule…</div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {jobs.map((j) => (
                <JobRow key={j.job_id} job={j} onSave={patchJob} busy={busy === j.job_id} />
              ))}
            </div>
          )}
        </Card>

        <div className="mt-6 text-xs text-ink-muted">
          <span className="font-heading">Categories:</span>{" "}
          {categories.map((c) => (
            <span key={c.id} className="mr-3">
              <span className="font-mono">{c.id}</span> · {c.description}
            </span>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}

function JobRow({ job, onSave, busy }) {
  const [enabled, setEnabled] = useState(job.enabled);
  const [hour, setHour] = useState(job.hour_utc ?? "");
  const [dow, setDow] = useState(job.day_of_week ?? "");
  const [minute, setMinute] = useState(job.minute ?? 0);
  const dirty = enabled !== job.enabled
    || String(hour) !== String(job.hour_utc ?? "")
    || String(dow) !== String(job.day_of_week ?? "")
    || Number(minute) !== Number(job.minute ?? 0);

  const save = () => {
    const patch = { enabled };
    if (job.cadence !== "Hourly") patch.hour_utc = hour === "" ? null : Number(hour);
    if (job.cadence === "Weekly") patch.day_of_week = dow === "" ? null : Number(dow);
    patch.minute = Number(minute) || 0;
    onSave(job.job_id, patch);
  };

  return (
    <div className="py-4 flex items-start justify-between gap-3" data-testid={`schedule-job-${job.job_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Mail size={13} className="text-emerald-800" />
          <span className="font-heading text-ink">{job.label}</span>
          <Pill tone={enabled ? "success" : "muted"}>{enabled ? "Enabled" : "Paused"}</Pill>
          <Pill tone="muted">{job.cadence}</Pill>
          <Pill tone="gold">{job.category}</Pill>
        </div>
        <div className="text-xs text-ink-soft">{job.description}</div>
        <div className="text-[10px] font-mono text-ink-muted mt-1">
          Recipients: {job.recipients} · Job ID: <code>{job.job_id}</code>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <label className="inline-flex items-center gap-1 text-xs" data-testid={`schedule-enabled-${job.job_id}`}>
          <input type="checkbox" checked={!!enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
        {job.cadence !== "Hourly" && (
          <div className="flex items-center gap-1 text-xs">
            <Clock size={11} className="text-ink-muted" />
            <input type="number" min={0} max={23} value={hour}
              onChange={(e) => setHour(e.target.value)}
              className="w-14 px-2 py-1 rounded border border-navy/15 text-xs font-mono"
              placeholder="hh"
              data-testid={`schedule-hour-${job.job_id}`} />
            <span className="text-ink-muted">:</span>
            <input type="number" min={0} max={59} value={minute}
              onChange={(e) => setMinute(e.target.value)}
              className="w-14 px-2 py-1 rounded border border-navy/15 text-xs font-mono"
              placeholder="mm"
              data-testid={`schedule-minute-${job.job_id}`} />
            <span className="text-ink-muted">UTC</span>
          </div>
        )}
        {job.cadence === "Weekly" && (
          <select value={dow} onChange={(e) => setDow(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-navy/15"
            data-testid={`schedule-dow-${job.job_id}`}>
            {DOW_LABELS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        )}
        <button onClick={save} disabled={!dirty || busy}
          className={`px-2 py-1 rounded text-[11px] font-heading inline-flex items-center gap-1 ${dirty ? "border border-emerald-400 text-emerald-800 hover:bg-emerald-50" : "border border-navy/15 text-ink-muted"}`}
          data-testid={`schedule-save-${job.job_id}`}>
          <Save size={12} /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
